import { useState, useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Users, Shield, Award, Calculator, Home, Zap, Euro } from "lucide-react";
import { SEO } from "@/components/seo/SEO";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";

interface CalculatorData {
  postcode: string;
  jaarverbruik: number;
  gasverbruik?: number;
  dakOrientatie: string;
  huisType: string;
  afbetalingVoorkeur: string;
  aanbevolenPanelen: number;
  jaarProduktie: number;
  maandBesparing: number;
  jaarBesparing: number;
  pakketPrijs: number;
  terugverdientijd: number;
  maandAflossing?: number;
  calculatedAt: string;
  source: string;
}

export default function OfferteSnell() {
  const { toast } = useToast();
  const [calculatorData, setCalculatorData] = useState<CalculatorData | null>(null);
  const [formData, setFormData] = useState({
    voornaam: "",
    achternaam: "",
    email: "",
    telefoon: "",
    adres: "",
    postcode: "",
    plaats: "",
    bericht: "",
    privacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Load calculator data from localStorage
    const savedData = localStorage.getItem('calculatorData');
    
    // Load quote context from local landing pages
    const quoteContext = localStorage.getItem('quoteContext');
    let contextData = null;
    if (quoteContext) {
      try {
        contextData = JSON.parse(quoteContext);
      } catch (error) {
        console.error('Error loading quote context:', error);
      }
    }
    
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        setCalculatorData(data);
        
        // Pre-fill address fields with calculator data
        setFormData(prev => ({
          ...prev,
          postcode: data.postcode || "",
          bericht: contextData ? `Interesse in ${contextData.serviceName} voor ${contextData.locationName}.\n\n` : ""
        }));
        
        track("offerte_snel_loaded_with_data", {
          source: data.source,
          investment: data.pakketPrijs,
          localService: contextData?.service,
          localLocation: contextData?.location
        });
      } catch (error) {
        console.error('Error loading calculator data:', error);
        track("offerte_snel_loaded_without_data", {
          localService: contextData?.service,
          localLocation: contextData?.location
        });
      }
    } else if (contextData) {
      // Pre-fill with service and location context when no calculator data
      setFormData(prev => ({
        ...prev,
        bericht: `Interesse in ${contextData.serviceName} voor ${contextData.locationName}.\n\nIk zou graag een persoonlijke offerte ontvangen voor de installatie van ${contextData.serviceName.toLowerCase()} bij mijn woning in ${contextData.locationName}.`
      }));
      
      track("offerte_snel_loaded_from_local_page", {
        service: contextData.service,
        location: contextData.location,
        source: contextData.source
      });
    } else {
      track("offerte_snel_loaded_without_data");
    }
  }, []);

  const formatCalculatorDataForComments = (data: CalculatorData): string => {
    const roofTypeLabels: Record<string, string> = {
      'tile-roof': 'Hellend dak met pannen',
      'slate-roof': 'Hellend dak met leien',
      'metal-roof': 'Hellend dak met metaal',
      'flat-roof': 'Plat dak',
      'other': 'Andere bedekking'
    };

    const houseTypeLabels: Record<string, string> = {
      'rijtjes': 'Rijtjeshuis',
      'twee_onder_een_kap': 'Twee-onder-een-kap',
      'vrijstaand': 'Vrijstaand huis',
      'appartement': 'Appartement'
    };

    return `CALCULATOR GEGEVENS:
• Postcode: ${data.postcode}
• Jaarverbruik: ${data.jaarverbruik} kWh
• Gasverbruik: ${data.gasverbruik || 'Niet opgegeven'} m³
• Woningtype: ${houseTypeLabels[data.huisType] || data.huisType}
• Dakoriëntatie: ${roofTypeLabels[data.dakOrientatie] || data.dakOrientatie}
• Afbetalingsvoorkeur: ${data.afbetalingVoorkeur}

BEREKENDE RESULTATEN:
• Aanbevolen panelen: ${data.aanbevolenPanelen}
• Jaarproductie: ${(data.jaarProduktie || 0).toLocaleString()} kWh
• Maandelijkse besparing: €${data.maandBesparing}
• Jaarlijkse besparing: €${data.jaarBesparing}
• Terugverdientijd: ${data.terugverdientijd} jaar
• Pakketprijs: €${(data.pakketPrijs || 0).toLocaleString()}

Berekend op: ${new Date(data.calculatedAt).toLocaleDateString('nl-NL')}

AANVULLENDE OPMERKINGEN:`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacy) {
      toast({
        title: "Privacy akkoord vereist",
        description: "U moet akkoord gaan met de privacy voorwaarden om verder te gaan.",
        variant: "destructive"
      });
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Track submission attempt
      track("offerte_snel_submit_attempt", {
        hasCalculatorData: !!calculatorData,
        pakketPrijs: calculatorData?.pakketPrijs
      });

      // Prepare quote data for email
      const quoteData = {
        // Personal Info
        voornaam: formData.voornaam,
        achternaam: formData.achternaam,
        email: formData.email,
        telefoon: formData.telefoon,
        adres: formData.adres,
        postcode: formData.postcode || calculatorData?.postcode || "",
        plaats: formData.plaats,
        
        // Calculator Data
        jaarverbruik: calculatorData?.jaarverbruik || 0,
        gasverbruik: calculatorData?.gasverbruik,
        roofType: calculatorData?.dakOrientatie || "",
        huisType: calculatorData?.huisType || "",
        
        // Results
        aanbevolenPanelen: calculatorData?.aanbevolenPanelen || 0,
        maandBesparing: calculatorData?.maandBesparing || 0,
        jaarBesparing: calculatorData?.jaarBesparing || 0,
        pakketPrijs: calculatorData?.pakketPrijs || 0,
        afbetalingVoorkeur: calculatorData?.afbetalingVoorkeur || "",
        terugverdientijd: calculatorData?.terugverdientijd || 0,
        jaarProduktie: calculatorData?.jaarProduktie || 0,
        maandAflossing: calculatorData?.maandAflossing || 0,
        
        // Metadata
        calculatedAt: calculatorData?.calculatedAt || new Date().toISOString(),
        source: calculatorData?.source || 'direct_form',
        bericht: formData.bericht
      };

      // Send email via Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('send-quote-email', {
        body: quoteData
      });

      if (error) {
        throw error;
      }

      // Success
      track("offerte_snel_submit_success", {
        pakketPrijs: calculatorData?.pakketPrijs
      });

      toast({
        title: "Offerte aanvraag verzonden! ✅",
        description: "We nemen binnen 24 uur contact met u op voor uw persoonlijke offerte."
      });

      // Clear form
      setFormData({
        voornaam: "",
        achternaam: "",
        email: "",
        telefoon: "",
        adres: "",
        postcode: calculatorData?.postcode || "",
        plaats: "",
        bericht: "",
        privacy: false
      });
      
    } catch (error: any) {
      console.error('Error submitting quote:', error);
      
      track("offerte_snel_submit_error", {
        error: error.message
      });

      toast({
        title: "Er ging iets mis",
        description: "Probeer het opnieuw of bel ons direct: 06 17 10 57 86",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="py-20">
        <SEO
          title="Snelle offerte op basis van uw berekening | SOL-VI"
          description="Ontvang direct een persoonlijke offerte gebaseerd op uw zonneplan berekening."
          type="website"
        />
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <CheckCircle className="h-4 w-4" />
              Stap 2 van 3: Persoonlijke gegevens
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Ontvang Uw Persoonlijke Offerte</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Gebaseerd op uw berekening vullen we alvast de technische details in. 
              U hoeft alleen nog uw contactgegevens toe te voegen.
            </p>
          </div>

          {/* Calculator Data Preview */}
          {calculatorData && (
            <div className="max-w-4xl mx-auto mb-8">
              <Card className="shadow-energy bg-gradient-to-r from-primary/5 to-secondary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-primary" />
                    Uw Zonneplan Overzicht
                  </CardTitle>
                  <CardDescription>
                    Deze gegevens nemen we automatisch over in uw offerte
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-background/50 rounded-lg">
                      <Home className="h-6 w-6 text-primary mx-auto mb-1" />
                      <div className="text-lg font-bold">{calculatorData.aanbevolenPanelen}</div>
                      <div className="text-xs text-muted-foreground">Panelen</div>
                    </div>
                    <div className="text-center p-3 bg-background/50 rounded-lg">
                      <Zap className="h-6 w-6 text-secondary mx-auto mb-1" />
                      <div className="text-lg font-bold">{(calculatorData.jaarProduktie || 0).toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">kWh/jaar</div>
                    </div>
                    <div className="text-center p-3 bg-background/50 rounded-lg">
                      <Euro className="h-6 w-6 text-accent mx-auto mb-1" />
                      <div className="text-lg font-bold">€{calculatorData.maandBesparing}</div>
                      <div className="text-xs text-muted-foreground">Maand voordeel</div>
                    </div>
                    <div className="text-center p-3 bg-background/50 rounded-lg">
                      <Calculator className="h-6 w-6 text-green-600 mx-auto mb-1" />
                      <div className="text-lg font-bold">{calculatorData.terugverdientijd}</div>
                      <div className="text-xs text-muted-foreground">Jaar terug</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-2xl">Contactgegevens</CardTitle>
                  <CardDescription>
                    Vul uw gegevens in voor uw persoonlijke offerte
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Persoonlijke gegevens */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">Persoonlijke Gegevens</h3>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="voornaam">Voornaam *</Label>
                          <Input
                            id="voornaam"
                            value={formData.voornaam}
                            onChange={(e) => setFormData(prev => ({ ...prev, voornaam: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="achternaam">Achternaam *</Label>
                          <Input
                            id="achternaam"
                            value={formData.achternaam}
                            onChange={(e) => setFormData(prev => ({ ...prev, achternaam: e.target.value }))}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="email">E-mailadres *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="telefoon">Telefoonnummer *</Label>
                          <Input
                            id="telefoon"
                            type="tel"
                            value={formData.telefoon}
                            onChange={(e) => setFormData(prev => ({ ...prev, telefoon: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Adresgegevens */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">Adresgegevens</h3>
                      
                      <div>
                        <Label htmlFor="adres">Straat en huisnummer *</Label>
                        <Input
                          id="adres"
                          value={formData.adres}
                          onChange={(e) => setFormData(prev => ({ ...prev, adres: e.target.value }))}
                          required
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="postcode">Postcode *</Label>
                          <Input
                            id="postcode"
                            value={formData.postcode}
                            onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                            placeholder={calculatorData?.postcode || "1234 AB"}
                            required
                          />
                          {calculatorData?.postcode && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Uit calculator: {calculatorData.postcode}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="plaats">Plaats *</Label>
                          <Input
                            id="plaats"
                            value={formData.plaats}
                            onChange={(e) => setFormData(prev => ({ ...prev, plaats: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    </div>

                     {/* Bericht */}
                     <div>
                       <Label htmlFor="bericht">Aanvullende opmerkingen (optioneel)</Label>
                       <textarea
                         id="bericht"
                         value={formData.bericht}
                         onChange={(e) => setFormData(prev => ({ ...prev, bericht: e.target.value }))}
                         rows={4}
                         className="w-full p-3 border rounded-md text-sm"
                         placeholder="Eventuele extra informatie of vragen..."
                       />
                        <p className="text-xs text-muted-foreground mt-1">
                          Uw calculator gegevens worden automatisch meegestuurd in de e-mail.
                        </p>
                     </div>

                    {/* Privacy */}
                    <div className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id="privacy"
                        checked={formData.privacy}
                        onChange={(e) => setFormData(prev => ({ ...prev, privacy: e.target.checked }))}
                        className="mt-1"
                      />
                      <Label htmlFor="privacy" className="text-sm leading-relaxed">
                        Ik ga akkoord met de verwerking van mijn gegevens voor het opstellen van een offerte 
                        en eventuele toekomstige communicatie van SOL-VI. *
                      </Label>
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Verzenden..." : "Verstuur Offerte Aanvraag"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Info Sidebar */}
            <div className="space-y-6">
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-secondary" />
                    Uw Voordelen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Calculator gegevens al verwerkt</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Persoonlijke offerte binnen 24 uur</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Gratis technisch advies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Geen verplichtingen</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Waarom SOL-VI?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-secondary" />
                    <div>
                      <div className="font-semibold">500+ Klanten</div>
                      <div className="text-sm text-muted-foreground">Tevreden en energieneutraal</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Award className="h-8 w-8 text-primary" />
                    <div>
                      <div className="font-semibold">15+ Jaar Ervaring</div>
                      <div className="text-sm text-muted-foreground">Gecertificeerde installateurs</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Shield className="h-8 w-8 text-accent" />
                    <div>
                      <div className="font-semibold">5 Jaar Garantie</div>
                      <div className="text-sm text-muted-foreground">Op alle installaties</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-muted rounded-lg p-6 text-center">
                <h3 className="font-semibold mb-2">Direct Contact?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Liever persoonlijk contact? Bel ons direct!
                </p>
                <Button variant="outline" asChild className="w-full">
                  <a href="tel:0617105786">
                    06 17 10 57 86
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}